// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/tracking/ShortageReport.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { trackingAPI, warehouseAPI } from "../../services/api";
import SearchableSelect from "../../components/SearchableSelect";
import PersianDatePicker, { gregorianToJalaliStr } from "../../utils/PersianDatePicker";
import { AlertTriangle, Filter, Download, Package, Truck } from "lucide-react";

const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0);

export default function ShortageReport() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState<any[]>([]);
  const [shortages, setShortages] = useState<any[]>([]);
  const [byProduct, setByProduct] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const data: any = {};
    if (from) data.from = from;
    if (to) data.to = to;
    if (productId) data.product_id = parseInt(productId);
    const r = await trackingAPI.shortageReport(data);
    if (r.success) {
      setShortages(r.shortages || []);
      setByProduct(r.by_product || []);
    }
    setLoading(false);
  }, [from, to, productId]);

  const loadProducts = useCallback(async () => {
    const r = await warehouseAPI.productsList();
    if (r.success) setProducts(r.products || []);
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { load(); }, [load]);

  const exportToExcel = () => {
    if (shortages.length === 0) return;
    const headers = ["شماره بار", "گمرک", "تاریخ ورود", "کانتینر", "موتر", "جنس", "واحد", "مقدار اولیه", "مقدار رسیده", "کسری", "علت", "تاریخ توزیع", "مقصد"];
    const rows = shortages.map((s: any) => [
      s.shipment_serial,
      s.customs_office_name,
      s.entry_date,
      s.container_number || "",
      s.truck_plate || "",
      s.product_name,
      s.unit,
      s.expected_qty,
      s.received_qty,
      s.shortage,
      s.shortage_reason || "",
      s.distribution_date,
      s.destination_type === "warehouse" ? s.warehouse_name : s.customer_name,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `گزارش_کسری_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalShortageAll = byProduct.reduce((s: number, p: any) => s + parseFloat(p.total_shortage), 0);

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      
      {/* فیلترها */}
      <div style={{
        padding: 16, borderRadius: 12, marginBottom: 18,
        background: dark ? "rgba(255,255,255,0.03)" : "#fff",
        border: `1px solid ${T.cardBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Filter size={15} color="#ef4444" />
          <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>فیلتر گزارش</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr auto", gap: 12, alignItems: "end" }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>از تاریخ</label>
            <PersianDatePicker value={from} onChange={setFrom} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>تا تاریخ</label>
            <PersianDatePicker value={to} onChange={setTo} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>جنس</label>
            <SearchableSelect
              options={[{ value: "", label: "همه اجناس" }, ...products.map((p: any) => ({ value: String(p.id), label: p.name }))]}
              value={productId}
              onChange={setProductId}
              placeholder="انتخاب جنس"
            />
          </div>
          <button onClick={exportToExcel} disabled={shortages.length === 0} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "10px 18px", borderRadius: 10, border: "none",
            background: shortages.length === 0 ? "#94a3b8" : "linear-gradient(135deg,#10b981,#059669)",
            color: "#fff", cursor: shortages.length === 0 ? "not-allowed" : "pointer",
            fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}>
            <Download size={14} /> Excel
          </button>
        </div>
      </div>

      {/* خلاصه آماری */}
      {byProduct.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 12 }}>📊 خلاصه کسری به تفکیک جنس</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12 }}>
            {byProduct.map((p: any) => (
              <div key={p.product_id} style={{
                padding: 14, borderRadius: 11,
                background: dark ? "rgba(239,68,68,0.05)" : "#fef2f2",
                border: "1px solid rgba(239,68,68,0.2)",
                borderRight: "4px solid #ef4444",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <Package size={16} color="#ef4444" />
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{p.product_name}</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 900, color: "#ef4444" }}>{fmt(p.total_shortage)}</span>
                  <span style={{ fontSize: 11, color: T.subtext, fontWeight: 700 }}>{p.unit}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{
            marginTop: 12, padding: "10px 16px", borderRadius: 10,
            background: dark ? "rgba(239,68,68,0.1)" : "#fee2e2",
            border: "1px solid rgba(239,68,68,0.3)",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <AlertTriangle size={18} color="#ef4444" />
            <span style={{ fontSize: 13, color: T.text }}>
              <strong style={{ color: "#ef4444" }}>مجموع کل کسری‌ها:</strong> {fmt(totalShortageAll)} (در {shortages.length} ردیف)
            </span>
          </div>
        </div>
      )}

      {/* لیست کسری‌ها */}
      <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 12 }}>📋 جزئیات کسری‌ها</div>
      
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>
      ) : shortages.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext, background: dark ? "rgba(16,185,129,0.05)" : "#f0fdf4", borderRadius: 12, border: "1px solid rgba(16,185,129,0.2)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#10b981" }}>هیچ کسری‌ای یافت نشد</div>
        </div>
      ) : (
        <div style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${T.cardBorder}` }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "100px 100px 130px 140px 130px 1fr 80px 90px 90px 90px 110px 1fr",
            gap: 8, padding: "12px 14px",
            background: dark ? "rgba(239,68,68,0.1)" : "#fef2f2",
            borderBottom: `1px solid ${T.cardBorder}`,
            fontSize: 11, fontWeight: 800, color: "#ef4444",
          }}>
            <span>سریال بار</span>
            <span>ش. بار</span>
            <span>گمرک</span>
            <span>کانتینر</span>
            <span>موتر</span>
            <span>جنس</span>
            <span style={{ textAlign: "center" }}>اولیه</span>
            <span style={{ textAlign: "center" }}>رسیده</span>
            <span style={{ textAlign: "center" }}>کسری</span>
            <span style={{ textAlign: "center" }}>تاریخ</span>
            <span>مقصد</span>
            <span>علت</span>
          </div>
          {shortages.map((s: any, i: number) => (
            <div key={s.id} style={{
              display: "grid",
              gridTemplateColumns: "100px 100px 130px 140px 130px 1fr 80px 90px 90px 90px 110px 1fr",
              gap: 8, padding: "11px 14px",
              borderBottom: i < shortages.length - 1 ? `1px solid ${T.cardBorder}` : "none",
              background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)"),
              fontSize: 11, color: T.text, alignItems: "center",
            }}>
              <span style={{ fontWeight: 700, color: "#3b82f6", fontFamily: "monospace" }}>{s.shipment_serial}</span>
              <span>{s.bar_number || "—"}</span>
              <span style={{ color: T.subtext }}>{s.customs_office_name}</span>
              <span style={{ fontFamily: "monospace", color: T.subtext }}>{s.container_number || "—"}</span>
              <span style={{ fontFamily: "monospace", color: T.subtext }}>{s.truck_plate || "—"}</span>
              <span style={{ fontWeight: 700 }}>{s.product_name}</span>
              <span style={{ textAlign: "center", color: T.text }}>{fmt(s.expected_qty)}</span>
              <span style={{ textAlign: "center", color: "#10b981" }}>{fmt(s.received_qty)}</span>
              <span style={{ textAlign: "center", color: "#ef4444", fontWeight: 800, fontSize: 12 }}>{fmt(s.shortage)}</span>
              <span style={{ textAlign: "center", color: T.subtext }}>{gregorianToJalaliStr(s.distribution_date)}</span>
              <span style={{ color: T.subtext }}>
                {s.destination_type === "warehouse" ? `🏭 ${s.warehouse_name}` : `👤 ${s.customer_name}`}
              </span>
              <span style={{ color: T.subtext, fontStyle: "italic", fontSize: 10 }}>{s.shortage_reason || "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}